import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const STATUS_CONFIG = {
  operational: { label: 'All systems operational', color: '#3ECFA4', bg: 'rgba(62,207,164,0.1)' },
  degraded:    { label: 'Partial degradation',     color: '#F5C067', bg: 'rgba(245,192,103,0.1)' },
  outage:      { label: 'Service outage',           color: '#F47676', bg: 'rgba(244,118,118,0.1)' },
};

export default function StatusPage() {
  const { orgId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const res = await fetch(`${apiUrl}/api/status/${orgId}`);
        if (!res.ok) throw new Error('Status page not found');
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [orgId]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#080A10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#080A10', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7E86AA' }}>
      <p>Status page not found.</p>
    </div>
  );

  const overall = STATUS_CONFIG[data.overallStatus] || STATUS_CONFIG.operational;

  return (
    <div style={{ minHeight: '100vh', background: '#080A10', color: '#E8ECFF', fontFamily: 'DM Sans, sans-serif', padding: '48px 24px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ width: 28, height: 28, background: '#7C6FE0', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>IP</div>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{data.orgName}</span>
          <span style={{ fontSize: 12, color: '#454D6D', marginLeft: 'auto' }}>Status page</span>
        </div>

        <div style={{ background: overall.bg, border: `1px solid ${overall.color}33`, borderRadius: 12, padding: '20px 24px', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: overall.color, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: overall.color }}>{overall.label}</div>
            <div style={{ fontSize: 12, color: '#7E86AA', marginTop: 2 }}>Updated {new Date().toLocaleTimeString()}</div>
          </div>
        </div>

        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 13, fontWeight: 500, color: '#454D6D', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Services</h2>
          <div style={{ background: '#0D0F18', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' }}>
            {data.integrations.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#454D6D', fontSize: 13 }}>No services configured</div>
            ) : data.integrations.map((item, i) => {
              const s = item.status;
              const color = s === 'healthy' ? '#3ECFA4' : s === 'down' ? '#F47676' : s === 'slow' ? '#F5C067' : '#7E86AA';
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: i < data.integrations.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 12, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{item.name}</div>
                    {item.description && <div style={{ fontSize: 11, color: '#454D6D', marginTop: 1 }}>{item.description}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#454D6D' }}>Uptime</div>
                      <div style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', color: item.uptime >= 99 ? '#3ECFA4' : item.uptime >= 95 ? '#F5C067' : '#F47676' }}>{item.uptime}%</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#454D6D' }}>Response</div>
                      <div style={{ fontSize: 12, fontFamily: 'DM Mono, monospace', color: '#7E86AA' }}>{item.avgResponseTime}ms</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: `${color}18`, color, fontFamily: 'DM Mono, monospace', fontWeight: 500 }}>{s}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {data.incidents && data.incidents.length > 0 && (
          <div>
            <h2 style={{ fontSize: 13, fontWeight: 500, color: '#454D6D', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Recent incidents</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.incidents.slice(0, 5).map((inc, i) => (
                <div key={i} style={{ background: '#0D0F18', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#F47676', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 12, color: '#7E86AA' }}><strong style={{ color: '#E8ECFF' }}>{inc.integrationName}</strong> went down</div>
                  <div style={{ fontSize: 11, color: '#454D6D', fontFamily: 'DM Mono, monospace' }}>{new Date(inc.createdAt).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 48, textAlign: 'center', fontSize: 12, color: '#454D6D' }}>
          Powered by <a href="https://integratepulse.web.app" style={{ color: '#7C6FE0', textDecoration: 'none' }}>IntegratePulse</a>
        </div>
      </div>
    </div>
  );
}
